module.exports = function createNotificationSound({ storageKey, initialKeys = [] }) {
    const controls = Array.from(document.querySelectorAll('[data-admin-notification-sound-toggle]'));
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const playedKey = `${storageKey}:played`;
    let enabled = true;
    let preferenceInMemory = false;
    let context = null;
    let previousKeys = new Set(initialKeys);
    let playedInThisPage = [];

    const readPreference = () => {
        if (preferenceInMemory) {
            return enabled;
        }
        try {
            return window.localStorage.getItem(storageKey) !== 'off';
        } catch (error) {
            return enabled;
        }
    };

    const renderControls = () => {
        controls.forEach((button) => {
            button.disabled = !AudioContext;
            button.setAttribute('aria-checked', enabled && AudioContext ? 'true' : 'false');
            const icon = button.querySelector('[data-notification-sound-icon]');
            icon.classList.toggle('bi-volume-up', enabled);
            icon.classList.toggle('bi-volume-mute', !enabled);
            button.querySelector('[data-notification-sound-status]').textContent = !AudioContext
                ? 'Non disponibile in questo browser'
                : `${enabled ? 'Attivo' : 'Disattivato'} su questo browser`;
        });
    };

    // Audio can only be unlocked by interacting with the page. Do not replay
    // notifications received before that interaction or while sound was muted.
    const unlock = () => {
        if (!enabled || !AudioContext) {
            return;
        }
        try {
            if (!context || context.state === 'closed') {
                context = new AudioContext();
            }
            if (context.state !== 'running') {
                context.resume().catch(() => {});
            }
        } catch (error) {
            // The browser may still block audio; visual notifications keep working.
        }
    };

    const mute = () => {
        if (context?.state === 'running') {
            context.suspend().catch(() => {});
        }
    };

    const chime = () => {
        const start = context.currentTime;
        [880, 1174.66].forEach((frequency, index) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            const time = start + index * 0.13;
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(frequency, time);
            gain.gain.setValueAtTime(0, time);
            gain.gain.linearRampToValueAtTime(0.075, time + 0.012);
            gain.gain.exponentialRampToValueAtTime(0.001, time + 0.24);
            oscillator.connect(gain);
            gain.connect(context.destination);
            oscillator.onended = () => {
                oscillator.disconnect();
                gain.disconnect();
            };
            oscillator.start(time);
            oscillator.stop(time + 0.25);
        });
    };

    const playOnce = (newKeys) => {
        const claimAndPlay = () => {
            if (!enabled || !readPreference() || context?.state !== 'running') {
                return;
            }
            let played = playedInThisPage;
            try {
                const stored = JSON.parse(window.localStorage.getItem(playedKey) || '[]');
                if (Array.isArray(stored)) {
                    played = stored;
                }
            } catch (error) {
                // Storage can be unavailable without disabling sound in this page.
            }
            if (newKeys.every((key) => played.includes(key))) {
                return;
            }
            chime();
            // Bound shared history while retaining recent events across reloads/tabs.
            playedInThisPage = [...new Set([...played, ...newKeys])].slice(-1000);
            try {
                window.localStorage.setItem(playedKey, JSON.stringify(playedInThisPage));
            } catch (error) {}
        };

        // Serialize the check/write across tabs so one arrival produces one chime.
        const locks = window.navigator?.locks;
        if (locks) {
            locks.request(playedKey, claimAndPlay).catch(() => {});
        } else {
            try {
                claimAndPlay();
            } catch (error) {}
        }
    };

    enabled = readPreference();
    renderControls();
    controls.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            enabled = !enabled;
            try {
                window.localStorage.setItem(storageKey, enabled ? 'on' : 'off');
                preferenceInMemory = false;
            } catch (error) {
                preferenceInMemory = true;
            }
            renderControls();
            if (enabled) {
                unlock();
            } else {
                mute();
            }
        });
    });
    document.addEventListener('pointerdown', unlock, { capture: true });
    document.addEventListener('keydown', unlock, { capture: true });
    window.addEventListener('storage', (event) => {
        if (event.key === storageKey || event.key === null) {
            enabled = readPreference();
            renderControls();
            if (!enabled) {
                mute();
            }
        }
    });

    return {
        update(keys) {
            const currentKeys = new Set(keys);
            const newKeys = [...currentKeys].filter((key) => !previousKeys.has(key));
            previousKeys = currentKeys;
            if (newKeys.length > 0 && enabled && context?.state === 'running') {
                playOnce(newKeys);
            }
        },
    };
};
